import { applySeo } from "@/lib/seo-head";
import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Layout } from "@/components/site/Layout";
import { listProductsPublic, getPublicSettings, type PublicProduct } from "@/lib/content.functions";
import { ArrowRight, Loader2 } from "lucide-react";
import { useT } from "@/lib/i18n";

function formatPrice(cents: number, currency: string, lang: string) {
  const locale = lang === "pt" ? "pt-BR" : lang === "en" ? "en-GB" : "es-ES";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

const productsListQueryOptions = queryOptions({
  queryKey: ["products-public"],
  queryFn: () => listProductsPublic(),
});

export const Route = createFileRoute("/services/paquetes")({
  component: Paquetes,
  loader: async ({ context }) => {
    const data = await context.queryClient.ensureQueryData(productsListQueryOptions);
    const products = (data?.products ?? []).filter((p) => p.show_in_catalog !== false);
    return { products };
  },
  head: (ctx) => applySeo(ctx.matches, (({ loaderData }) => {
    const data = loaderData as { products?: PublicProduct[] } | undefined;
    const url = "https://rrlexjuristas.com/services/paquetes";
    const products = data?.products ?? [];
    const itemListJsonLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Paquetes con precio cerrado — RR Lex Juristas",
      url,
      itemListElement: products.map((p, i) => {
        const item: Record<string, unknown> = {
          "@type": "Product",
          name: p.name,
          description: p.description,
          url: `https://rrlexjuristas.com/products/${p.slug}`,
          brand: { "@type": "Organization", name: "RR Lex Juristas" },
        };
        if (!p.priceless && p.price_cents > 0) {
          item.offers = {
            "@type": "Offer",
            price: (p.price_cents / 100).toFixed(2),
            priceCurrency: (p.currency || "EUR").toUpperCase(),
            availability: "https://schema.org/InStock",
          };
        }
        return {
          "@type": "ListItem",
          position: i + 1,
          item,
        };
      }),
    };
    return {
      meta: [
        { title: "Paquetes con precio cerrado — RR Lex Juristas" },
        { name: "description", content: "Trámites jurídicos con alcance definido y precio único. Sin sorpresas y con seguimiento personalizado." },
        { property: "og:title", content: "Paquetes con precio cerrado — RR Lex Juristas" },
        { property: "og:description", content: "Servicios cerrados, precio único y proceso guiado paso a paso." },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(itemListJsonLd) },
      ],
    };
  })(ctx)),
});

function Paquetes() {
  const { tk, lang } = useT();
  const list = useServerFn(listProductsPublic);
  const getSettings = useServerFn(getPublicSettings);
  const { data, isLoading } = useQuery({
    queryKey: ["products-public"],
    queryFn: () => list(),
  });
  const { data: settingsData } = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => getSettings(),
  });
  const products = (data?.products ?? []).filter((p) => p.show_in_catalog !== false);
  const consultationEnabled = settingsData?.settings.consultation_payment_enabled ?? true;

  return (
    <Layout>
      <section>
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <span className="text-[10px] tracking-[0.4em] uppercase text-gold">{tk("servicios.fe6b29", "Paquetes")}</span>
            <h1 className="font-serif text-4xl md:text-5xl mt-4">
              {tk("servicios.2c16b9", "Trámites cerrados, ")}
              <em className="not-italic gold-gradient">{tk("servicios.0bcb23", "precio único")}</em>
            </h1>
            <p className="mt-5 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {tk("servicios.65a43c", "Servicios con alcance definido y precio fijo. Sin sorpresas y con seguimiento personalizado.")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {isLoading && (
              <div className="col-span-full flex justify-center py-12 text-muted-foreground">
                <Loader2 className="animate-spin" />
              </div>
            )}
            {consultationEnabled && (
              <div className="rounded-2xl border border-gold/30 bg-card/30 p-8 flex flex-col">
                <div className="text-[10px] tracking-[0.3em] uppercase text-gold/70">{tk("servicios.4feb51", "Consulta")}</div>
                <h2 className="font-serif text-2xl mt-3">{tk("servicios.d933a3", "Consulta jurídica de 45 min")}</h2>
                <p className="text-sm text-muted-foreground mt-4 leading-relaxed flex-1">
                  {tk("servicios.e9d315", "Sesión personalizada para analizar tu caso, resolver dudas y orientarte sobre los próximos pasos. Confirmación inmediata por email.")}
                </p>
                <Link
                  to="/contact"
                  className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-primary-foreground px-6 py-3 text-xs tracking-[0.3em] uppercase hover:bg-[oklch(0.88_0.08_85)] transition-colors"
                >
                  {tk("servicios.900e6d", "Reservar")} <ArrowRight size={12} />
                </Link>
              </div>
            )}
            {products.map((p) => (
              <div
                key={p.slug}
                className="rounded-2xl border border-gold/20 bg-card/30 p-8 flex flex-col"
              >
                <div className="text-[10px] tracking-[0.3em] uppercase text-gold/70">{tk("servicios.cf08aa", "Paquete")}</div>
                <h2 className="font-serif text-2xl mt-3">{tk(`products.${p.slug}.name`, p.name)}</h2>
                {!p.priceless && (
                  <div className="text-3xl gold-gradient font-serif mt-4">
                    {formatPrice(p.price_cents, p.currency, lang)}
                  </div>
                )}
                <p className="text-sm text-muted-foreground mt-4 leading-relaxed flex-1">
                  {tk(`products.${p.slug}.description`, p.description)}
                </p>
                <Link
                  to="/products/$slug"
                  params={{ slug: p.slug }}
                  className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-primary-foreground px-6 py-3 text-xs tracking-[0.3em] uppercase hover:bg-[oklch(0.88_0.08_85)] transition-colors"
                >
                  {tk("servicios.b73399", "Contratar")} <ArrowRight size={12} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
